import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartTooltipShell } from "../panel";

const AXIS_COLOR = "var(--neutral-inactive)";

export interface WaterfallStep {
  label: string;
  /** the step's own increment; the running total is computed here */
  value: number;
  color: string;
}

interface WaterfallDatum {
  label: string;
  base: number;
  span: number;
  value: number;
  total: number;
  color: string;
  isTotal: boolean;
}

function buildData(steps: WaterfallStep[], total: { label: string; color: string }) {
  const rows: WaterfallDatum[] = [];
  let running = 0;
  for (const step of steps) {
    const start = running;
    const end = running + step.value;
    rows.push({
      label: step.label,
      base: Math.min(start, end),
      span: Math.abs(step.value),
      value: step.value,
      total: end,
      color: step.color,
      isTotal: false,
    });
    running = end;
  }
  rows.push({
    label: total.label,
    base: 0,
    span: Math.max(running, 0),
    value: running,
    total: running,
    color: total.color,
    isTotal: true,
  });
  return rows;
}

function WaterfallTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: WaterfallDatum }>;
}) {
  const datum = payload?.[0]?.payload;
  if (!active || !datum) return null;
  return (
    <ChartTooltipShell>
      <p className="font-semibold text-foreground">{datum.label}</p>
      <p className="mt-1 flex items-center gap-3 text-muted-foreground">
        {datum.isTotal ? "Total" : "Adds"}
        <span className="ml-auto tabular-nums text-foreground">
          {datum.isTotal ? "" : datum.value >= 0 ? "+" : ""}
          {datum.value.toFixed(2)}%
        </span>
      </p>
      {datum.isTotal ? null : (
        <p className="flex items-center gap-3 text-muted-foreground">
          Running Total
          <span className="ml-auto tabular-nums text-foreground">{datum.total.toFixed(2)}%</span>
        </p>
      )}
    </ChartTooltipShell>
  );
}

export function WaterfallChart({
  steps,
  total,
  height = 300,
}: {
  steps: WaterfallStep[];
  total: { label: string; color: string };
  height?: number;
}) {
  const data = buildData(steps, total);

  return (
    <div style={{ height }} className="w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="2 4" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: AXIS_COLOR, fontSize: 11 }}
            stroke={AXIS_COLOR}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: AXIS_COLOR, fontSize: 11 }}
            stroke={AXIS_COLOR}
            width={56}
            tickFormatter={(value: number) => `${value.toFixed(1)}%`}
          />
          <Tooltip
            content={<WaterfallTooltip />}
            cursor={{ fill: "var(--border)", fillOpacity: 0.25 }}
          />
          <Bar dataKey="base" stackId="w" fill="transparent" stroke="none" isAnimationActive={false} />
          <Bar dataKey="span" stackId="w" isAnimationActive={false}>
            {data.map((datum) => (
              <Cell key={datum.label} fill={datum.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
